import rjBirdRateCardAuthority from '../../backend/src/shared/pricing-core/rate-cards/rj-bird-rate-card-authority.json';
import { isPerDayPricingUnit } from '../utils/pricing-unit-semantics.js';
import { costingConfigForEditor } from './pricing-costing-editor.js';
import { hasEstimateProvenance, isTrustedPricingItem, withoutEstimateMarkers } from './pricing-trust.js';

const RJ_BIRD_LEGAL_NAME = 'rj bird building pty ltd';

const CATEGORY_SPECS = [
    { key: 'material_costs', nameField: 'item', rateField: 'cost' },
    { key: 'labour_rates', nameField: 'trade', rateField: 'rate' },
    { key: 'equipment', nameField: 'item', rateField: 'cost' },
];

const ROW_OVERRIDE_FIELDS = ['item', 'trade', 'unit', 'cost', 'rate', 'costRate'];

// Both spellings of a row's sell-rate GST basis. Readers take the snake_case
// one first, so an approved row's basis must replace both, never sit beside the
// authority's.
const SELL_TAX_BASIS_FIELDS = ['charge_out_tax_basis', 'chargeOutTaxBasis'];

// JSON cloning drops apiFetch's non-enumerable snake_case aliases. Read the
// canonical camelCase response as well as the original stored shape.
function fieldValue(row, field) {
    const camel = field.replace(/_([a-z0-9])/g, (_, letter) => letter.toUpperCase());
    return row?.[field] !== undefined ? row[field] : row?.[camel];
}

function clone(value) {
    if (value === undefined) return undefined;
    return JSON.parse(JSON.stringify(value));
}

export function normaliseTenantIdentityName(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function isRjBirdTenantProfile(profile) {
    if (!profile || typeof profile !== 'object') return false;
    return [profile.legalName, profile.tradingName]
        .map(normaliseTenantIdentityName)
        .includes(RJ_BIRD_LEGAL_NAME);
}

function normaliseCatalogueName(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function uniqueStrings(values) {
    return [...new Set((Array.isArray(values) ? values : []).map(value => String(value || '').trim()).filter(Boolean))];
}

function getRowOverrides(row) {
    const explicit = uniqueStrings(row?.operator_overrides || row?.operatorOverrides);
    if (explicit.length > 0) return explicit;

    const source = String(row?.source || '').toLowerCase();
    const history = fieldValue(row, 'price_history');
    const hasHistory = Array.isArray(history) && history.length > 0;
    const created = fieldValue(row, 'created_at');
    const updated = fieldValue(row, 'updated_at');
    const timestampsDiffer = created && updated && created !== updated;
    const looksOperatorOwned = source === 'manual'
        || source === 'user_confirmed'
        || hasHistory
        || timestampsDiffer;
    return looksOperatorOwned ? ROW_OVERRIDE_FIELDS.filter(field => row?.[field] !== undefined) : [];
}

function mergeAliases(authorityRow, currentRow) {
    return uniqueStrings([...(authorityRow?.aliases || []), ...(currentRow?.aliases || [])]);
}

function mergeAuthorityRow(authorityRow, currentRow, spec) {
    if (!currentRow) return clone(authorityRow);

    const overrideFields = getRowOverrides(currentRow);
    let merged = {
        ...clone(authorityRow),
        aliases: mergeAliases(authorityRow, currentRow),
    };

    // Usage, review history, and audit timestamps belong to the tenant even
    // when the bundled authority is refreshed.
    [
        'usage_count', 'usageCount', 'last_used_at', 'lastUsedAt',
        'price_history', 'previous_rate', 'previous_rate_2',
        'created_at', 'updated_at', 'reviewed_at', 'reviewed_by',
    ].forEach(field => {
        const value = fieldValue(currentRow, field);
        if (value !== undefined) merged[field] = clone(value);
    });

    // Business choices and supplier evidence are not published authority rates.
    // They survive refresh even if the row's sell rate has never been edited.
    [
        ['preferredBasis', 'preferred_basis'], ['catalogueCategory', 'catalogue_category'],
        ['contractorName', 'contractor_name'], ['supplier', 'supplier'],
        ['supplierUrl', 'supplier_url'], ['supplierSku', 'supplier_sku'],
        ['supplierPack', 'supplier_pack'], ['priceCheckedAt', 'price_checked_at'],
        ['costStatus', 'cost_status'],
    ].forEach(([canonical, alternate]) => {
        const value = currentRow[canonical] !== undefined ? currentRow[canonical] : currentRow[alternate];
        if (value !== undefined) merged[canonical] = clone(value);
    });

    overrideFields.forEach(field => {
        const value = fieldValue(currentRow, field);
        if (value !== undefined) merged[field] = clone(value);
    });

    if (overrideFields.length > 0) {
        merged.operator_overrides = uniqueStrings(overrideFields);
        merged.updated_at = fieldValue(currentRow, 'updated_at') || new Date().toISOString();
    }

    // The builder approved this row's AI Review estimate (pricing-trust.js
    // approveEstimatePricingItem). That approval is the tenant's decision, like
    // an edited rate: refreshing the authority must not mark the row as an
    // estimate again, or every quote line that names it goes back to unpriced.
    //
    // The authority still calls its own rate an estimate, so a rate it publishes
    // later is a new estimate, not a price the builder approved. The approval
    // therefore keeps the builder's sell terms too: the rate, its unit and the
    // GST basis it was approved on. An explicit override list that does not
    // name the rate (a business cost recorded before the approval) would
    // otherwise let the authority's rate through as trusted. A missing basis is
    // read as inc GST (pricing-match-applier.js _matchedRateMetadata), so it
    // stays missing rather than taking the authority's ex_gst.
    const estimateApproval = fieldValue(currentRow, 'estimate_approval');
    if (estimateApproval && isTrustedPricingItem(currentRow) && hasEstimateProvenance(merged)) {
        merged = {
            ...withoutEstimateMarkers(merged),
            source: currentRow.source,
            review_status: fieldValue(currentRow, 'review_status') || 'approved',
            estimate_approval: clone(estimateApproval),
        };
        const approvedFields = [spec.rateField, 'unit'].filter(field => fieldValue(currentRow, field) !== undefined);
        approvedFields.forEach(field => { merged[field] = clone(fieldValue(currentRow, field)); });
        SELL_TAX_BASIS_FIELDS.forEach(field => { delete merged[field]; });
        SELL_TAX_BASIS_FIELDS.forEach(field => {
            if (currentRow[field] !== undefined) merged[field] = clone(currentRow[field]);
        });
        merged.operator_overrides = uniqueStrings([...(merged.operator_overrides || []), ...approvedFields]);
    }

    // The stable authority ID is never overridden. Match consumers can keep
    // using the exact-ID fast path even when the tenant changes the visible
    // name, unit, charge-out rate, or supplier cost.
    merged.id = authorityRow.id;
    merged.pricing_category = spec.key;
    return merged;
}

function readPath(object, path) {
    return String(path || '').split('.').reduce((value, key) => value?.[key], object);
}

function writePath(object, path, value) {
    const keys = String(path || '').split('.').filter(Boolean);
    let target = object;
    keys.slice(0, -1).forEach(key => {
        if (!target[key] || typeof target[key] !== 'object') target[key] = {};
        target = target[key];
    });
    if (keys.length > 0) target[keys[keys.length - 1]] = clone(value);
}

function mergePricingMethodology(currentMethodology) {
    const merged = clone(rjBirdRateCardAuthority.pricing_methodology);
    const overrides = uniqueStrings(currentMethodology?.operatorOverrides || currentMethodology?.operator_overrides);
    overrides.forEach(path => {
        const value = readPath(currentMethodology, path);
        if (value !== undefined) writePath(merged, path, value);
    });
    if (overrides.length > 0) merged.operatorOverrides = overrides;
    return merged;
}

function pricingAuthorityMetadata() {
    const authority = rjBirdRateCardAuthority;
    return {
        schema_version: authority.schema_version,
        authority_id: authority.authority_id,
        source_file: authority.source_file,
        source_sha256: authority.source_sha256,
        source_semantics: clone(authority.source_semantics),
        counts: clone(authority.counts),
    };
}

// A worker's full day and the same trade's hour are different rates with the
// same name. Matching a row to the authority by NAME alone would let "Electrician
// / Day Rate" absorb (and lose) the tenant's "Electrician / Per hour" row, so a
// name match also has to agree on whether the row is billed per day.
function billedPerDay(row) {
    return isPerDayPricingUnit(row?.unit, row?.is_day_rate === true || row?.isDayRate === true);
}

function catalogueNameKey(row, spec) {
    return normaliseCatalogueName(row?.[spec.nameField] || row?.item || row?.trade || row?.name);
}

const OVERRIDE_FIELD_FOR_LABOUR = Object.freeze({ item: 'trade', cost: 'rate' });

// The material-shaped row a tenant holds today, in the labour shape it is moving
// to. Values are carried, never recomputed: item -> trade, cost (the sell field)
// -> rate, and an explicit operator-override list is renamed to match so the
// operator's edited sell price and business cost survive the move.
function asLabourShapedRow(materialRow) {
    const { item, cost, includes_labour: _includesLabour, ...rest } = materialRow;
    const translated = { ...rest, trade: materialRow.trade ?? item, rate: materialRow.rate ?? cost };
    const explicit = materialRow.operator_overrides || materialRow.operatorOverrides;
    if (Array.isArray(explicit)) {
        translated.operator_overrides = uniqueStrings(explicit.map(field => OVERRIDE_FIELD_FOR_LABOUR[field] || field));
        delete translated.operatorOverrides;
    }
    return translated;
}

/**
 * The authority rows whose stable id the tenant holds in a DIFFERENT list than
 * the authority now files them in (the trade day rates that moved from
 * Materials to Labour). Only rows the authority explicitly marks `relocated_from`
 * move, and only from that list. If the tenant already holds the id in the
 * right list too, nothing moves: the stale copy is reported, never dropped.
 *
 * Pure and read-only: this is the reviewable diff, before any apply.
 */
export function planRateCardRelocations(currentData) {
    const moves = new Map();
    const conflicts = [];
    CATEGORY_SPECS.forEach(spec => {
        const authorityRows = Array.isArray(rjBirdRateCardAuthority[spec.key]) ? rjBirdRateCardAuthority[spec.key] : [];
        const targetRows = Array.isArray(currentData?.[spec.key]) ? currentData[spec.key] : [];
        authorityRows.forEach(authorityRow => {
            const from = authorityRow?.relocated_from;
            if (!from || from === spec.key) return;
            const sourceRows = Array.isArray(currentData?.[from]) ? currentData[from] : [];
            const id = String(authorityRow.id);
            const byId = sourceRows.find(row => row && String(row.id ?? '') === id);
            // A row from before stable ids existed is matched the way the sync always matched it
            // (by name), and only to a row on the same day basis.
            const authorityName = normaliseCatalogueName(authorityRow.trade || authorityRow.item);
            const idLessCandidates = byId ? [] : sourceRows.filter(row => row && !String(row.id ?? '').trim()
                && catalogueNameKey(row, { nameField: 'item' }) === authorityName
                && billedPerDay(row) === billedPerDay(authorityRow));
            if (idLessCandidates.length > 1) {
                // Two look-alikes with no id cannot be told apart. Moving the first would choose between them,
                // so neither moves; both stay exactly where they are and the operator is told.
                conflicts.push({
                    id,
                    from,
                    to: spec.key,
                    name: String(authorityRow.trade || authorityRow.item || id),
                    reason: 'ambiguous-no-id',
                    count: idLessCandidates.length,
                });
                return;
            }
            const source = byId || idLessCandidates[0];
            if (!source) return;
            if (targetRows.some(row => row && String(row.id ?? '') === id)) {
                if (byId) conflicts.push({ id, from, to: spec.key, name: String(source.item || source.trade || source.name || id) });
                return;
            }
            moves.set(`${spec.key}\u0000${id}`, { id, from, to: spec.key, source });
        });
    });
    return { moves, conflicts };
}

function mergeCategory(currentData, spec, summary, plan) {
    const authorityRows = Array.isArray(rjBirdRateCardAuthority[spec.key])
        ? rjBirdRateCardAuthority[spec.key]
        : [];
    const currentRows = Array.isArray(currentData?.[spec.key]) ? currentData[spec.key] : [];
    const movingOut = new Set([...plan.moves.values()].map(move => move.source));
    const currentById = new Map(currentRows.filter(row => row?.id).map(row => [String(row.id), row]));
    const currentByName = new Map();
    currentRows.forEach(row => {
        if (movingOut.has(row)) return;
        const name = catalogueNameKey(row, spec);
        const key = `${name}\u0000${billedPerDay(row) ? 'day' : 'other'}`;
        if (name && !currentByName.has(key)) currentByName.set(key, row);
    });

    const usedRows = new Set();
    const mergedAuthorityRows = authorityRows.map(authorityRow => {
        const authorityName = normaliseCatalogueName(authorityRow?.[spec.nameField]);
        const nameKey = `${authorityName}\u0000${billedPerDay(authorityRow) ? 'day' : 'other'}`;
        const move = plan.moves.get(`${spec.key}\u0000${String(authorityRow.id)}`);
        const sameListRow = currentById.get(String(authorityRow.id));
        const currentRow = sameListRow
            || (move ? asLabourShapedRow(move.source) : null)
            || currentByName.get(nameKey);
        if (!currentRow) {
            summary.added += 1;
            return clone(authorityRow);
        }
        usedRows.add(currentRow);
        if (move && !sameListRow) {
            summary.relocated += 1;
            summary.relocations.push({
                id: move.id,
                name: String(authorityRow[spec.nameField] || move.id),
                from: move.from,
                to: move.to,
            });
        }
        const overrides = getRowOverrides(currentRow);
        if (overrides.length > 0) summary.manualRowsPreserved += 1;
        else if (!(move && !sameListRow)) summary.refreshed += 1;
        return mergeAuthorityRow(authorityRow, currentRow, spec);
    });

    const customRows = currentRows.filter(row => !usedRows.has(row) && !movingOut.has(row));
    summary.customRowsKept += customRows.length;
    return [...mergedAuthorityRows, ...clone(customRows)];
}

// Two live rows for the same trade on the same day basis make a name lookup
// ambiguous. The sync never merges or deletes one to hide that: it names them.
function findPossibleDuplicateDayRates(rows) {
    const seen = new Map();
    const duplicates = [];
    (Array.isArray(rows) ? rows : []).forEach(row => {
        if (!row || !billedPerDay(row)) return;
        const key = normaliseCatalogueName(row.trade || row.item || row.name);
        if (!key) return;
        if (seen.has(key)) duplicates.push({ name: String(row.trade || row.item || row.name), ids: [seen.get(key), String(row.id ?? '')] });
        else seen.set(key, String(row.id ?? ''));
    });
    return duplicates;
}

export function buildRjBirdRateCardSyncPreview(currentData, tenantProfile) {
    if (!isRjBirdTenantProfile(tenantProfile)) {
        return {
            eligible: false,
            needsSync: false,
            mergedData: null,
            authority: pricingAuthorityMetadata(),
            summary: {
                added: 0, refreshed: 0, manualRowsPreserved: 0, customRowsKept: 0,
                relocated: 0, relocations: [], relocationConflicts: [], possibleDuplicates: [],
            },
        };
    }

    const current = clone(currentData || {});
    const plan = planRateCardRelocations(current);
    const summary = {
        added: 0, refreshed: 0, manualRowsPreserved: 0, customRowsKept: 0,
        relocated: 0, relocations: [], relocationConflicts: plan.conflicts, possibleDuplicates: [],
    };
    const mergedData = { ...current };
    CATEGORY_SPECS.forEach(spec => {
        mergedData[spec.key] = mergeCategory(current, spec, summary, plan);
    });
    summary.possibleDuplicates = findPossibleDuplicateDayRates(mergedData.labour_rates);

    const pricingMethodology = mergePricingMethodology(costingConfigForEditor(current?.costingConfig)?.pricingMethodology);
    const authority = pricingAuthorityMetadata();
    mergedData.costingConfig = {
        ...(current.costingConfig || {}),
        pricingMethodology,
        pricingAuthority: authority,
    };
    mergedData.administration = {
        ...(current.administration || {}),
        quote_administration_fee: pricingMethodology.administration_fee_ex_gst_tiers?.[0]?.amount_ex_gst || 0,
        tiers: clone(pricingMethodology.administration_fee_ex_gst_tiers || []),
        note: 'Project administration follows the Francis total-value tiers and is allocated equally across dependent scope items.',
    };
    mergedData.rateCardAuthority = authority;
    mergedData.updated_at = new Date().toISOString();

    const currentAuthority = current?.costingConfig?.pricingAuthority || current?.rateCardAuthority || {};
    const authorityIdsComplete = CATEGORY_SPECS.every(spec => {
        const currentIds = new Set((current?.[spec.key] || []).map(row => row?.id).filter(Boolean));
        return (rjBirdRateCardAuthority[spec.key] || []).every(row => currentIds.has(row.id));
    });
    const needsSync = fieldValue(currentAuthority, 'authority_id') !== authority.authority_id
        || fieldValue(currentAuthority, 'schema_version') !== authority.schema_version
        || fieldValue(currentAuthority, 'source_sha256') !== authority.source_sha256
        || !authorityIdsComplete;

    return { eligible: true, needsSync, mergedData, authority, summary };
}

export function getRjBirdBundledRateCardAuthority() {
    return clone(rjBirdRateCardAuthority);
}
